import React, { useMemo } from 'react';
import { AutoComplete, Checkbox, Input, InputNumber, Table, Tag, Tooltip, Typography } from 'antd';
import { RobotOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { MenuDraftItem } from '../../types/menuIngest';
import { MENU_DRAFT_FLAGS } from '../../types/menuIngest';

const { Text } = Typography;

interface Props {
  items: MenuDraftItem[];
  onChange: (id: string, patch: Partial<MenuDraftItem>) => void;
  onFocusPage: (item: MenuDraftItem) => void;
  focusedId: string | null;
  compact: boolean;
}

/**
 * Cómo se explica cada marca. El texto va en la tooltip y no en la celda: son
 * ochenta renglones y la columna tiene que poder leerse de un vistazo.
 */
const FLAG_LABELS: Record<string, { label: string; color: string; help: string }> = {
  [MENU_DRAFT_FLAGS.yaExiste]: {
    label: 'Ya existe',
    color: 'default',
    help: 'Ya tienes un producto con este nombre en esta categoría. No se va a crear de nuevo.',
  },
  [MENU_DRAFT_FLAGS.duplicado]: {
    label: 'Repetido',
    color: 'default',
    help: 'Aparece dos veces en esta carga. Sólo se crea el primero.',
  },
  [MENU_DRAFT_FLAGS.precioIlegible]: {
    label: 'Sin precio',
    color: 'error',
    help: 'No se pudo leer el precio. Escríbelo o quita el renglón.',
  },
  [MENU_DRAFT_FLAGS.precioFueraDeRango]: {
    label: 'Precio raro',
    color: 'error',
    help: 'Se leyó un precio imposible, así que se dejó en blanco. Escríbelo a mano.',
  },
  [MENU_DRAFT_FLAGS.variantePorTamano]: {
    label: 'Por tamaño',
    color: 'processing',
    help: 'Este platillo tenía varios precios en la carta y se abrió en un producto por tamaño.',
  },
  [MENU_DRAFT_FLAGS.textoRecortado]: {
    label: 'Recortado',
    color: 'warning',
    help: 'El texto era más largo de lo que cabe y se recortó. Vale la pena revisarlo.',
  },
};

/** Un renglón que no se va a crear se muestra apagado, pero se muestra. */
const isSkipped = (item: MenuDraftItem) =>
  item.excluded ||
  item.flags.includes(MENU_DRAFT_FLAGS.yaExiste) ||
  item.flags.includes(MENU_DRAFT_FLAGS.duplicado);

/**
 * La tabla de revisión: el corazón de la feature.
 *
 * Todo es editable en el lugar. La alternativa —abrir un modal por renglón— es
 * inviable con una carta de ochenta productos, que es el caso normal y no el
 * extremo.
 *
 * **Lo que dijo la IA y lo que escribiste tú se distinguen siempre**, con el
 * icono de la primera columna. Es el innegociable de la feature: quien confirma
 * tiene que poder ver de dónde salió cada dato.
 */
const MenuDraftTable: React.FC<Props> = ({ items, onChange, onFocusPage, focusedId, compact }) => {
  // Las categorías que ya aparecen en el borrador se ofrecen como sugerencia: al
  // corregir una mal leída, lo más probable es que la correcta ya esté escrita en
  // otro renglón, y volver a teclearla invita a crear "Bebidas" y "bebidas".
  const categoryOptions = useMemo(
    () =>
      Array.from(new Set(items.map((i) => i.categoryName)))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'es'))
        .map((value) => ({ value })),
    [items]
  );

  const columns = [
    {
      title: '',
      key: 'source',
      width: 40,
      render: (_: unknown, item: MenuDraftItem) => {
        const meta = {
          Ia: { icon: <RobotOutlined />, help: 'Lo leyó la IA de tu carta' },
          Editado: { icon: <EditOutlined />, help: 'Lo corregiste tú' },
          Agregado: { icon: <PlusOutlined />, help: 'Lo agregaste tú' },
        }[item.source];

        return (
          <Tooltip title={meta.help}>
            <span style={{ opacity: item.source === 'Ia' ? 0.45 : 0.9 }}>{meta.icon}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Producto',
      key: 'name',
      render: (_: unknown, item: MenuDraftItem) => (
        <Input
          value={item.name}
          onChange={(e) => onChange(item.id, { name: e.target.value })}
          onFocus={() => onFocusPage(item)}
          variant="borderless"
          // Lo que la IA marcó como dudoso se subraya: es lo primero que hay que
          // mirar en una lista larga, y sin señal visual nadie lo encuentra.
          style={{
            padding: 0,
            fontWeight: 500,
            textDecoration:
              item.source === 'Ia' && item.nameConfidence === 'Low' ? 'underline dotted' : undefined,
          }}
        />
      ),
    },
    ...(compact
      ? []
      : [
          {
            title: 'Descripción',
            key: 'description',
            render: (_: unknown, item: MenuDraftItem) => (
              <Input
                value={item.description ?? ''}
                onChange={(e) => onChange(item.id, { description: e.target.value || null })}
                onFocus={() => onFocusPage(item)}
                placeholder="—"
                variant="borderless"
                style={{ padding: 0 }}
              />
            ),
          },
        ]),
    {
      title: 'Categoría',
      key: 'category',
      width: compact ? 130 : 180,
      render: (_: unknown, item: MenuDraftItem) => (
        <AutoComplete
          value={item.categoryName}
          options={categoryOptions}
          onChange={(value) => onChange(item.id, { categoryName: value })}
          onFocus={() => onFocusPage(item)}
          filterOption={(input, option) =>
            (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
          variant="borderless"
        />
      ),
    },
    {
      title: 'Precio',
      key: 'price',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, item: MenuDraftItem) => (
        <InputNumber
          value={item.price}
          onChange={(value) => onChange(item.id, { price: value ?? null })}
          onFocus={() => onFocusPage(item)}
          min={0}
          max={9999.99}
          precision={2}
          prefix="$"
          placeholder="—"
          variant="borderless"
          status={item.price === null ? 'error' : undefined}
          // tabular-nums: los precios de una columna tienen que alinearse para
          // que un dígito de más salte a la vista.
          style={{
            width: '100%',
            fontVariantNumeric: 'tabular-nums',
            textDecoration:
              item.source === 'Ia' && item.priceConfidence === 'Low'
                ? 'underline dotted'
                : undefined,
          }}
        />
      ),
    },
    {
      title: 'Notas',
      key: 'flags',
      width: compact ? 90 : 180,
      render: (_: unknown, item: MenuDraftItem) => (
        <span>
          {item.flags.map((flag) => {
            const meta = FLAG_LABELS[flag];
            if (!meta) return null;
            return (
              <Tooltip key={flag} title={meta.help}>
                <Tag color={meta.color} style={{ marginInlineEnd: 4 }}>
                  {meta.label}
                </Tag>
              </Tooltip>
            );
          })}
        </span>
      ),
    },
    {
      title: <Tooltip title="Los renglones sin palomita no se crean">Crear</Tooltip>,
      key: 'include',
      width: 64,
      align: 'center' as const,
      render: (_: unknown, item: MenuDraftItem) => {
        // La marca que BLOQUEA, no la primera que tenga etiqueta: un renglón que ya
        // existe y además viene con el precio ilegible explicaría "escribe el precio",
        // que no es lo que hay que hacer y encima sugiere que la casilla se va a
        // habilitar al hacerlo.
        const blockingFlag = [MENU_DRAFT_FLAGS.yaExiste, MENU_DRAFT_FLAGS.duplicado].find((f) =>
          item.flags.includes(f)
        );

        return (
          <Tooltip title={blockingFlag ? FLAG_LABELS[blockingFlag].help : undefined}>
            <Checkbox
              checked={!item.excluded && !blockingFlag}
              disabled={Boolean(blockingFlag)}
              onChange={(e) => onChange(item.id, { excluded: !e.target.checked })}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Table
      dataSource={items}
      columns={columns}
      rowKey="id"
      size="small"
      pagination={items.length > 25 ? { pageSize: 25, size: 'small' } : false}
      scroll={{ x: 'max-content' }}
      onRow={(item) => ({
        onClick: () => onFocusPage(item),
        style: {
          opacity: isSkipped(item) ? 0.5 : 1,
          background: item.id === focusedId ? 'var(--ant-color-fill-quaternary)' : undefined,
        },
      })}
      locale={{
        emptyText: (
          <Text type="secondary">
            Todavía no hay productos. Sube al menos una página de tu carta.
          </Text>
        ),
      }}
    />
  );
};

export default MenuDraftTable;
